/**
 * Student Grade Options API Contract (SCH-1288, plan 2026-10-01-1411 U6).
 *
 * `GET /v1/schools/:school_public_id/student-grade-options`
 *
 * Feeds the admin grade picker for `chat.studentGradeLevels`: the school's
 * grades, grouped under the school level each belongs to, with how many ACTIVE
 * students are seated in each grade.
 *
 * - Grades: every ACTIVE grade, plus any inactive grade still stored in the
 *   school's chat grade list (flagged `inactive`) so the admin can see and
 *   remove it.
 * - Grouping is display only. There is no FK from a grade to its level; the
 *   grade's `description` (e.g. "Upper School") is joined to the level `name`,
 *   the same join the enrollment inquiry form uses. A grade that matches no
 *   level lands in the "No school level" group (`id: null`), always last.
 *   "Entire School" is never synthesized; it appears only when the school
 *   really has a level by that name and a grade matches it.
 * - Order: level `sortOrder`, then grade `sortOrder`, then the grade's numeric
 *   name (non-numeric names such as "PG" last).
 * - `studentsWithoutGradeCount` counts ACTIVE students at the school with no
 *   profile row or a null current grade. The grade list cannot reach them.
 */

import { z } from 'zod'

/** Path params for the list endpoint. */
export const ListStudentGradeOptionsRequestSchema = z.object({
  school_public_id: z.string().describe('School unique identifier')
})

export type ListStudentGradeOptionsRequest = z.infer<typeof ListStudentGradeOptionsRequestSchema>

/** One grade the admin can pick. */
export const StudentGradeOptionSchema = z.object({
  id: z.string().describe('Grade-level publicId: the value stored in `chat.studentGradeLevels`'),
  name: z.string().describe('Grade name, falling back to its abbreviation, then its Blackbaud id'),
  abbreviation: z.string().nullable(),
  studentCount: z.number().int().min(0).describe('ACTIVE students currently seated in this grade'),
  inactive: z
    .boolean()
    .describe('True for a grade Blackbaud no longer reports as active that is still in the stored chat list')
})

export type StudentGradeOption = z.infer<typeof StudentGradeOptionSchema>

/** A school level and its grades. `id: null` is the "No school level" group. */
export const StudentGradeOptionGroupSchema = z.object({
  id: z.string().nullable().describe('School-level publicId, or null for the "No school level" group'),
  name: z.string().describe('Level name, or "No school level"'),
  grades: z.array(StudentGradeOptionSchema)
})

export type StudentGradeOptionGroup = z.infer<typeof StudentGradeOptionGroupSchema>

export const ListStudentGradeOptionsResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    groups: z.array(StudentGradeOptionGroupSchema),
    studentsWithoutGradeCount: z
      .number()
      .int()
      .min(0)
      .describe('ACTIVE students with no profile or no current grade; no grade choice reaches them')
  })
})

export type ListStudentGradeOptionsResponse = z.infer<typeof ListStudentGradeOptionsResponseSchema>
